// Mirrors backend/src/pdfTemplates/designA/designAFormatters.js's
// convertAmountToWordsDesignA exactly. Kept separate from the existing
// convertNumberToWords in ./functions.js (which every non-Design-A voucher
// still uses, unchanged) because that one always uses Indian grouping with a
// currency-code suffix regardless of country -- wrong for Design A's
// per-country phrasing (spec: India gets "Indian Rupees ... Only"; everyone
// else gets their own currency's name + Western grouping).
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function threeDigitWords(n) {
  if (n === 0) return "";
  let words = "";
  if (n >= 100) {
    words += `${ONES[Math.floor(n / 100)]} Hundred `;
    n %= 100;
  }
  if (n >= 20) {
    words += `${TENS[Math.floor(n / 10)]} `;
    n %= 10;
  }
  if (n > 0) {
    words += `${ONES[n]} `;
  }
  return words.trim();
}

function integerToWordsIndian(n) {
  if (n === 0) return "Zero";
  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  const hundred = n;

  const parts = [];
  if (crore) parts.push(`${threeDigitWords(crore)} Crore`);
  if (lakh) parts.push(`${threeDigitWords(lakh)} Lakh`);
  if (thousand) parts.push(`${threeDigitWords(thousand)} Thousand`);
  if (hundred) parts.push(threeDigitWords(hundred));
  return parts.join(" ").trim();
}

function integerToWordsWestern(n) {
  if (n === 0) return "Zero";
  const billion = Math.floor(n / 1000000000);
  n %= 1000000000;
  const million = Math.floor(n / 1000000);
  n %= 1000000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  const hundred = n;

  const parts = [];
  if (billion) parts.push(`${threeDigitWords(billion)} Billion`);
  if (million) parts.push(`${threeDigitWords(million)} Million`);
  if (thousand) parts.push(`${threeDigitWords(thousand)} Thousand`);
  if (hundred) parts.push(threeDigitWords(hundred));
  return parts.join(" ").trim();
}

const CURRENCY_DISPLAY_NAMES = {
  AED: "UAE Dirhams", SAR: "Saudi Riyals", QAR: "Qatari Riyals", KWD: "Kuwaiti Dinars",
  BHD: "Bahraini Dinars", OMR: "Omani Rials", USD: "US Dollars", EUR: "Euros",
  GBP: "British Pounds", INR: "Indian Rupees",
};

const DECIMAL_UNIT_NAMES = {
  USD: "Cents", EUR: "Cents", GBP: "Pence", INR: "Paise", JPY: "Sen",
  AED: "Fils", SAR: "Halala", KWD: "Fils", BHD: "Fils", OMR: "Baisa", QAR: "Dirhams",
};

export function convertAmountToWordsDesignA(amountToWord, { country, currency }) {
  if (amountToWord == null || isNaN(amountToWord)) return "Zero";

  const isNegative = amountToWord < 0;
  const absAmount = Math.abs(Number(amountToWord));
  const rupees = Math.floor(absAmount);
  const subunits = Math.round((absAmount - rupees) * 100);

  const isIndia = (country || "").trim().toLowerCase() === "india";
  const currencyCode = (currency || "").toUpperCase();

  const integerWords = isIndia ? integerToWordsIndian(rupees) : integerToWordsWestern(rupees);
  const currencyName = isIndia ? "Indian Rupees" : (CURRENCY_DISPLAY_NAMES[currencyCode] || currencyCode || "Amount");
  const subunitName = DECIMAL_UNIT_NAMES[currencyCode] || "Cents";

  let result = `${currencyName} ${integerWords}`;
  if (subunits > 0) {
    const subunitWords = isIndia ? integerToWordsIndian(subunits) : integerToWordsWestern(subunits);
    result += ` and ${subunitWords} ${subunitName}`;
  }
  result += " Only";

  return isNegative ? `Minus ${result}` : result;
}
